import { CloseOutlined, DownOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { ProFormField } from '@ant-design/pro-form';
import {
  Input,
  Dropdown,
  Space,
  Select,
  Button,
  Tooltip,
  SelectProps,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as uuid from 'uuid';
import { RulesType } from '@/presentation/components/form';
import styles from './index.module.scss';

export interface IProps {
  entry: RulesType;
}

export interface RulesFieldsItemProps {
  onDelete?: (index: string) => void;
  options: SelectProps['options'];
  value: RulesFieldsValue;
  onKeyChange: (key: string, value: RulesFieldsValue) => void;
}

export interface RulesFieldsPanelProps {
  defaultValue?: string;
  value?: RulesFormValue[];
  onChange?: (value?: RulesFormValue[]) => void;
  options: SelectProps['options'];
}


export type RulesFieldsValue = {
  operator?: SelectProps['value'];
  operatorLabel?: string | number | null | React.ReactNode;
  value?: string;
  key: string;
};

export type RulesFormValue = {
  operator?: SelectProps['value'];
  operatorLabel?: string | number | null | React.ReactNode;
  value?: string;
};


const RulesFieldsItem: React.FC<RulesFieldsItemProps> = (props) => {
  const { t } = useTranslation();
  const { options, value, onKeyChange } = props;

  const onDelete = () => {
    props.onDelete?.(value.key);
  };
  const onOperatorChange = (operatorValue: SelectProps['value']) => {
    onKeyChange(value.key, { ...value, operator: operatorValue.value, operatorLabel: operatorValue.label });
  };
  const valueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onKeyChange(value.key, { ...value, value: e.target.value });
  };

  return (
    <div
      className={styles.customForm}>
      <div
      >
        <Select
          labelInValue={true}
          value={value.operator}
          options={options}
          className={styles.operator}
          placeholder={t('请选择')}
          onChange={onOperatorChange}
        />
      </div>
      <div
        className={styles.values}
      >
        <Input
          value={value.value}
          placeholder={'Value'}
          onChange={valueChange}
        />
      </div>
      <CloseOutlined className={styles.close} onClick={onDelete} />
    </div>
  );
};

const RulesFieldsPanel: React.FC<RulesFieldsPanelProps> = (props) => {
  const { t } = useTranslation();
  const { onChange, value, options } = props;
  const [visible, setVisible] = useState(false);
  const [rulesFields, setRulesFields] = useState<RulesFieldsValue[] | undefined>([{ key: uuid.v4(), operator: options?.[0].value, operatorLabel: options?.[0].label, value: '' }]);
  const [filterText, setFilterText] = useState('');


  useEffect(() => {
    if (value && Array.isArray(value)) {
      setRulesFields(value.map(rule => ({ ...rule, key: uuid.v4() })));
      setFilterText(getFilterText(value));
    }
  }, [value]);

  const showOverlay = () => setVisible(true);
  const hideOverlay = () => {
    setVisible(false);
    if (value && Array.isArray(value) && value.length) {
      setRulesFields(value.map(rule => ({ ...rule, key: uuid.v4() })));
    } else {
      setRulesFields([{ key: uuid.v4(), operator: options?.[0].value, operatorLabel: options?.[0].label, value: '' }]);
    }
  };


  const onDelete = (key: string) => {
    setRulesFields(rules => rules?.filter?.(rule => rule.key !== key));
  };

  const onKeyChange = (key: string, value: RulesFieldsValue) => {
    setRulesFields(rules => rules?.map((rule) => {
      if (key === rule.key) {
        return value;
      }
      return rule;
    }));
  };

  const renderFilters = () => rulesFields?.map?.(filter => (
    <RulesFieldsItem onKeyChange={onKeyChange} value={filter} options={options} key={filter.key} onDelete={onDelete} />
  ));

  const addFilter = () => {
    setRulesFields(rules => [...(rules ?? []), { key: uuid.v4() }]);
  };


  const getFilterText = (values: (RulesFieldsValue | RulesFormValue)[]) => values.map((item) => {
    const operatorText = item.operatorLabel;
    return t('{{operator}}为{{value}}', { operator: operatorText, value: item.value });
  }).join(t(' 且 '));

  const onConfirm = async () => {
    try {
      setFilterText(getFilterText(rulesFields?.filter(rule => rule.value) || []));
      setVisible(false);
      onChange?.(rulesFields?.filter(rule => rule.value).map(rule => (
        { value: rule.value, operator: rule.operator, operatorLabel: rule.operatorLabel })));
    } catch (e) {
    }
  };

  const overlay = (
    <div className={styles.customOverlay}>
      <span>{t('以下的关系为全且')}</span>
      <div className={styles.itemContainer}>
        {renderFilters()}
      </div>
      <div>
        <Space className={styles.addButton} size={3}>
          <PlusCircleOutlined />
          <span onClick={addFilter}>{t('添加筛选')}</span>
        </Space>
      </div>
      <div className={styles.footer}>
        <Space size={10}>
          <Button onClick={hideOverlay}>{t('取消')}</Button>
          <Button type={'primary'} onClick={onConfirm}>{t('确定')}</Button>
        </Space>
      </div>
    </div>
  );


  return (
    <div className={styles.panel}>
      <Space size={0} className={styles.panelSpace} direction={'vertical'}>
        <Tooltip title={filterText}>
          <Input
            placeholder={t('请选择')}
            onFocus={showOverlay}
            suffix={<DownOutlined style={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '12px' }} />}
            style={{ width: '100%' }}
            value={filterText}
          />
        </Tooltip>
        <Dropdown
          overlay={overlay}
          trigger={['click']}
          visible={visible}
          destroyPopupOnHide={false}
          onVisibleChange={visible => setVisible(visible)}
        >
          <div style={{ height: 1 }} />
        </Dropdown>
      </Space>
    </div>
  );
};

export const RulesFields: React.FC<IProps> = (props) => {
  const { entry } = props;
  const { name, label, options, initialValue } = entry;

  return (
    <ProFormField name={name} label={label} initialValue={initialValue}>
      <RulesFieldsPanel options={options} />
    </ProFormField>
  );
};
